import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Progress } from '@/components/ui/progress';
import { Target, Calendar as CalendarIcon } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useHabits } from '@/hooks/use-habits';
import { HabitTracker } from '@/components/customer/HabitTracker';
import { CompetitionHabits } from '@/components/customer/CompetitionHabits';
import { useOrganization } from '@/hooks/use-organization';
import { useCustomerPermissions } from '@/hooks/useCustomerPermissions';

export function HabitsPage() {
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const { habits } = useHabits();
  const { selectedOrganization } = useOrganization();
  const { hasSectionAccess } = useCustomerPermissions(selectedOrganization?.id);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">{t('habits.habitTracking')}</h1>
        <p className="text-muted-foreground">
          {t('habits.description')}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr_300px]">
        <div className="space-y-6">
          <HabitTracker selectedDate={selectedDate} />
          {/* Same gate as the dashboard and mobile (namat#36). */}
          {hasSectionAccess('organizationCompetitions') && (
            <CompetitionHabits selectedDate={selectedDate} />
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                {t('habits.calendar')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md border"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                {t('habits.goals')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('habits.monthlyGoal')}</span>
                  <span>{habits.reduce((acc, habit) => acc + habit.daysCompleted, 0)}/30 {t('habits.days')}</span>
                </div>
                <Progress 
                  value={Math.min(100, (habits.reduce((acc, habit) => acc + habit.daysCompleted, 0) / 30) * 100)} 
                  className="h-2" 
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('habits.streakGoal')}</span>
                  <span>{Math.max(...habits.map(h => h.streak))}/10 {t('habits.days')}</span>
                </div>
                <Progress 
                  value={Math.min(100, (Math.max(...habits.map(h => h.streak)) / 10) * 100)} 
                  className="h-2" 
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}